import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave,
  faCalendarAlt,
  faCheckCircle,
  faClock,
  faFileInvoiceDollar,
  faPlus,
  faImage,
  faTag,
  faTimes,
  faMapMarkerAlt,
  faRulerCombined,
  faRupeeSign,
  faUser,
  faPhone,
  faEnvelope,
  faIdBadge,
  faReceipt,
  faWallet,
  faHistory,
  faLandmark,
  faPercentage
} from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from '../../utils';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';

const Payments = () => {
  const [showForm, setShowForm] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const userData = useSelector(state => state.user_info[0]?.user);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const getAllPropertiesofCurrentUser = async () => {
    setIsLoading(true);
    try {
      const response = await sendApiRequest('api/packages/all_manual/receipts', {}, 'GET');
      if (response.data.success) {
        const filteredReceipts = response.data.data.receipts
          .filter(receipt => receipt.customer.customerEmail === userData?.customerEmail && receipt.isCommissionReceipt === false)
          .map(receipt => ({
            _id: receipt._id,
            title: receipt.package.title,
            width: receipt.package.width,
            length: receipt.package.length,
            value: receipt.amount,
            paid_amount: receipt.paid_amount,
            balance_amount: receipt.balance_amount,
            location: receipt.remarks || 'Not specified',
            startDate: receipt.receiptDate,
            status: receipt.generatedManually ? 'Active' : 'Pending',
            receiptNumber: receipt.receiptNumber,
            customerName: receipt.customer.customerName,
            customerEmail: receipt.customer.customerEmail,
            customerPhone: receipt.customer.customerPhone,
            partnerName: receipt.partner.fullName,
            partnerEmail: receipt.partner.email,
            partnerPhone: receipt.partner.phone,
            paymentMode: receipt.paymentMode,
            paymentDetails: receipt.paymentDetails,
            amountInWords: receipt.amountInWords,
            createdAt: receipt.createdAt,
            updatedAt: receipt.updatedAt
          }));

        setTransactions(filteredReceipts);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to load properties',
        text: error.message,
        showConfirmButton: false,
        timer: 1500
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllPropertiesofCurrentUser();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Verified': return 'bg-green-100 text-green-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      case 'Active': return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Verified': return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-1" />;
      case 'Rejected': return <FontAwesomeIcon icon={faTimes} className="text-red-500 mr-1" />;
      case 'Active': return <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-1" />;
      default: return <FontAwesomeIcon icon={faClock} className="text-yellow-500 mr-1" />;
    }
  };

  console.log(userData)

  const toggleTransactionDetails = (id) => {
    if (expandedTransaction === id) {
      setExpandedTransaction(null);
    } else {
      setExpandedTransaction(id);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-green-500">
              Payment Transactions
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            View and manage all your property payment transactions in one place
          </p>
        </div>

       

        {/* Stats Cards */}
        {transactions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-50 text-blue-600 mr-4">
                  <FontAwesomeIcon icon={faRupeeSign} size="lg" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Paid</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(transactions.reduce((sum, txn) => sum + txn.paid_amount, 0))}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-50 text-green-600 mr-4">
                  <FontAwesomeIcon icon={faWallet} size="lg" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Balance Due</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(transactions.reduce((sum, txn) => sum + txn.balance_amount, 0))}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
                  <FontAwesomeIcon icon={faLandmark} size="lg" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Properties</p>
                  <p className="text-2xl font-semibold text-gray-900">{transactions.length}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transaction History */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-blue-500 mr-3" />
              Payment History
            </h3>
            <button
              onClick={getAllPropertiesofCurrentUser}
              className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Refresh
            </button>
          </div>

          {isLoading ? (
            <div className="py-12 flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
                <FontAwesomeIcon icon={faReceipt} size="3x" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-1">No transactions found</h4>
              <p className="text-gray-500 max-w-md mx-auto">
                You don't have any payment transactions yet. When you make a payment, it will appear here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {/* Desktop Table */}
              <div className="hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Property
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((txn) => (
                      <React.Fragment key={txn._id}>
                        <tr className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 rounded-md bg-blue-50 flex items-center justify-center text-blue-600">
                                <FontAwesomeIcon icon={faLandmark} />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{txn.title}</div>
                                <div className="text-xs text-gray-500 flex items-center mt-1">
                                  <FontAwesomeIcon icon={faRulerCombined} className="mr-1" />
                                  {txn.width} x {txn.length} ft
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div className="flex items-center">
                                <FontAwesomeIcon icon={faReceipt} className="mr-2 text-gray-400" />
                                {txn.receiptNumber}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                              {formatDate(txn.startDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              <FontAwesomeIcon icon={faRupeeSign} className="mr-1 text-gray-500" />
                              {formatCurrency(txn.paid_amount)}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Balance: {formatCurrency(txn.balance_amount)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(txn.status)}`}>
                              {getStatusIcon(txn.status)}
                              {txn.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => toggleTransactionDetails(txn._id)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              {expandedTransaction === txn._id ? 'Hide Details' : 'View Details'}
                            </button>
                          </td>
                        </tr>
                        {expandedTransaction === txn._id && (
                          <tr className="bg-gray-50">
                            <td colSpan="5" className="px-6 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                                    Customer Details
                                  </h4>
                                  <div className="bg-white p-4 rounded-lg shadow-xs border border-gray-100">
                                    <div className="mb-2">
                                      <span className="text-xs text-gray-500">Name:</span>
                                      <p className="text-sm font-medium">{txn.customerName}</p>
                                    </div>
                                    <div className="mb-2">
                                      <span className="text-xs text-gray-500">Email:</span>
                                      <p className="text-sm font-medium">{txn.customerEmail}</p>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500">Phone:</span>
                                      <p className="text-sm font-medium">{txn.customerPhone}</p>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                                    Partner Details
                                  </h4>
                                  <div className="bg-white p-4 rounded-lg shadow-xs border border-gray-100">
                                    <div className="mb-2">
                                      <span className="text-xs text-gray-500">Name:</span>
                                      <p className="text-sm font-medium">{txn.partnerName}</p>
                                    </div>
                                    <div className="mb-2">
                                      <span className="text-xs text-gray-500">Email:</span>
                                      <p className="text-sm font-medium">{txn.partnerEmail}</p>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500">Phone:</span>
                                      <p className="text-sm font-medium">{txn.partnerPhone}</p>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-gray-500" />
                                    Payment Details
                                  </h4>
                                  <div className="bg-white p-4 rounded-lg shadow-xs border border-gray-100">
                                    <div className="mb-2">
                                      <span className="text-xs text-gray-500">Mode:</span>
                                      <p className="text-sm font-medium capitalize">{txn.paymentMode}</p>
                                    </div>
                                    <div className="mb-2">
                                      <span className="text-xs text-gray-500">Reference:</span>
                                      <p className="text-sm font-medium">{txn.paymentDetails || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500">Amount in Words:</span>
                                      <p className="text-sm font-medium">{txn.amountInWords}</p>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-gray-500" />
                                    Property Details
                                  </h4>
                                  <div className="bg-white p-4 rounded-lg shadow-xs border border-gray-100">
                                    <div className="mb-2">
                                      <span className="text-xs text-gray-500">Location:</span>
                                      <p className="text-sm font-medium">{txn.location}</p>
                                    </div>
                                    <div className="mb-2">
                                      <span className="text-xs text-gray-500">Dimensions:</span>
                                      <p className="text-sm font-medium">{txn.width} x {txn.length} ft</p>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500">Total Value:</span>
                                      <p className="text-sm font-medium">{formatCurrency(txn.value)}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-gray-200">
                {transactions.map((txn) => (
                  <div key={txn._id} className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-md bg-blue-50 flex items-center justify-center text-blue-600">
                          <FontAwesomeIcon icon={faLandmark} />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-base font-medium text-gray-900">{txn.title}</h3>
                          <p className="text-sm text-gray-500 flex items-center mt-1">
                            <FontAwesomeIcon icon={faRulerCombined} className="mr-1" />
                            {txn.width} x {txn.length} ft
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(txn.status)}`}>
                        {getStatusIcon(txn.status)}
                        {txn.status}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Receipt No.</p>
                        <p className="text-sm font-medium text-gray-900">{txn.receiptNumber}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Date</p>
                        <p className="text-sm font-medium text-gray-900">{formatDate(txn.startDate)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Paid Amount</p>
                        <p className="text-sm font-medium text-gray-900">{formatCurrency(txn.paid_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Balance</p>
                        <p className="text-sm font-medium text-gray-900">{formatCurrency(txn.balance_amount)}</p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        onClick={() => toggleTransactionDetails(txn._id)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        {expandedTransaction === txn._id ? (
                          <>
                            <FontAwesomeIcon icon={faTimes} className="mr-1" />
                            Hide Details
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPlus} className="mr-1" />
                            View Details
                          </>
                        )}
                      </button>
                    </div>

                    {expandedTransaction === txn._id && (
                      <div className="mt-4 space-y-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                            <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                            Customer Details
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">Name</p>
                              <p className="text-sm font-medium">{txn.customerName}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="text-sm font-medium">{txn.customerEmail}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Phone</p>
                              <p className="text-sm font-medium">{txn.customerPhone}</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                            <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                            Partner Details
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">Name</p>
                              <p className="text-sm font-medium">{txn.partnerName}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="text-sm font-medium">{txn.partnerEmail}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Phone</p>
                              <p className="text-sm font-medium">{txn.partnerPhone}</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-gray-500" />
                            Payment Details
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-gray-500">Mode</p>
                              <p className="text-sm font-medium capitalize">{txn.paymentMode}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Reference</p>
                              <p className="text-sm font-medium">{txn.paymentDetails || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Amount in Words</p>
                              <p className="text-sm font-medium">{txn.amountInWords}</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-gray-500" />
                            Property Details
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">Location</p>
                              <p className="text-sm font-medium">{txn.location}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Dimensions</p>
                              <p className="text-sm font-medium">{txn.width} x {txn.length} ft</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Total Value</p>
                              <p className="text-sm font-medium">{formatCurrency(txn.value)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

         {/* Payment History Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <FontAwesomeIcon icon={faHistory} className="h-4 w-4 text-[#1DCD9F] mr-2" />
              Payment History
            </h3>
            <span className="text-xs bg-[#1DCD9F]/10 text-[#1DCD9F] px-2 py-1 rounded-full">
              {userData?.paymentHistory?.length || 0} payments
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remaining
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Mode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {userData?.paymentHistory?.map((payment, index) => (
                  <tr key={payment._id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(payment.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{payment.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{payment.totalAmount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{payment.remainingAmount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {payment.paymentMode}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payments;